import {
  amountFormatter,
  FinancialInsuranceProductCategory,
  labelForCommitment,
  labelForDetailsSection,
  labelForQuoteEmissione,
  labelForRealEconomyExplain,
  labelForWalletMacroCategoryType,
  ProductMacroCategory,
  walletPageMessages,
  walletCategoryMessages
} from 'azshared';
import { useIntl } from 'react-intl';
import { FinancialPositionsCategoryBox } from './financial-positions-category-box';
import React, { useCallback, useMemo } from 'react';
import walletCategoriesMessages from 'azshared/build/main/lib/messages/wallet/wallet-categories.messages';
import { AppSvgIcon } from '../../../../../components/svg-icon/app-svg-icon';
import Tooltip from '@mui/material/Tooltip';
import './financial-position-macrocategory-item.scss';

interface FinancialPositionsMacroCategoryItemProps {
  macroCategory: ProductMacroCategory;
  flagShowBalance: boolean;
  onGoToPrevinet: () => void;
  onGoToSustainableFuture: () => void;
  goToRealEconomyDetailPage: () => void;
  allMacroCategories?: ProductMacroCategory[];
}

export const FinancialPositionsMacroCategoryItem = (props: FinancialPositionsMacroCategoryItemProps) => {
  const { formatMessage } = useIntl();
  const { macroCategory, flagShowBalance, onGoToPrevinet, onGoToSustainableFuture, goToRealEconomyDetailPage, allMacroCategories } = props;

  const { type, amount, categories, commitment, quoteEmissione } = macroCategory;

  const { showFondiPensione, showSustainableFuture } = useMemo(() => {
    let showFondi = false;
    let showSustainable = false;

    if (type === 'Welfare' && categories) {
      for (const category of categories) {
        if (category.products) {
          for (const product of category.products) {
            if (product.contract) {
              if (product.contract.startsWith('43-00')) {
                showFondi = true;
              }
              if (product.contract.startsWith('43-01')) {
                showSustainable = true;
              }
            }
          }
        }
      }
    }
    return {
      showFondiPensione: showFondi,
      showSustainableFuture: showSustainable
    };
  }, [type, categories]);

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        goToRealEconomyDetailPage();
      }
    },
    [goToRealEconomyDetailPage]
  );

  const handlePrevinetClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onGoToPrevinet();
    },
    [onGoToPrevinet]
  );

  const handleKeyDownPrevinet = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onGoToPrevinet();
      }
    },
    [onGoToPrevinet]
  );

  const handleSustainableFutureClick = useCallback(
    (e: React.MouseEvent<HTMLAnchorElement>) => {
      e.preventDefault();
      onGoToSustainableFuture();
    },
    [onGoToSustainableFuture]
  );

  const handleKeyDownSustainableFuture = useCallback(
    (event: React.KeyboardEvent<HTMLAnchorElement>) => {
      if (event.key === 'Enter' || event.key === ' ') {
        event.preventDefault();
        onGoToSustainableFuture();
      }
    },
    [onGoToSustainableFuture]
  );

  const hasTotalFia = useMemo(() => {
    const hasFia = allMacroCategories?.some((cat) => cat.type === 'TotalFia');
    const hasRealEconomy = allMacroCategories?.some((cat) => cat.type === 'RealEconomy' && (cat.amount?.value > 0 || (cat.commitment?.value || 0) > 0));
    return hasFia && hasRealEconomy;
  }, [allMacroCategories]);

  const label = useMemo(() => {
    if (type === 'TotalFia') {
      return walletPageMessages.walletPaymentNotValidated;
    }
    if (type === 'TotalPortfolio') {
      return hasTotalFia ? walletCategoryMessages.macrocategoryTotalPortfolio : walletCategoryMessages.macrocategoryTotalPortfolioNoAsterisk;
    }
    return labelForWalletMacroCategoryType(type);
  }, [type, hasTotalFia]);

  return (
    <>
      <div className='financial-positions-item'>
        <div className='macro-categories'>
          <div>
            <div>
              <span style={{ marginRight: 5 }}>{formatMessage(label)}</span>
              {type === 'RealEconomy' && (
                <span className='app-link' onClick={goToRealEconomyDetailPage} onKeyDown={handleKeyDown} tabIndex={0}>
                  <br></br>
                  {formatMessage(labelForDetailsSection())}
                </span>
              )}
            </div>
          </div>
          <div className={`macro-categories-amount ${!flagShowBalance ? 'blurry-text' : ''}`}>{amount != null ? amountFormatter(amount) : ''}</div>
          {type === 'RealEconomy' && quoteEmissione && (
            <div className={'real-economy-quote-emissione'}>
              <span>{formatMessage(labelForQuoteEmissione())}</span>
              <span className='real-economy-note'>
                <sup>2</sup>
              </span>
              <span className={`real-economy-amount ${!flagShowBalance ? 'blurry-text' : ''}`}>{amountFormatter(quoteEmissione)}</span>
            </div>
          )}
          {type === 'RealEconomy' && commitment && (
            <div className={'real-economy-commitment'}>
              <span>{formatMessage(labelForCommitment())}</span>
              <span className={`real-economy-amount ${!flagShowBalance ? 'blurry-text' : ''}`}>{amountFormatter(commitment)}</span>
            </div>
          )}
          {type === 'Welfare' && (
            <div className='previnet-link'>
              {showFondiPensione && (
                <Tooltip title={formatMessage(walletPageMessages.previnetLinkMouseOverLabel)} arrow>
                  <a onClick={handlePrevinetClick} onKeyDown={handleKeyDownPrevinet} tabIndex={0} role='button'>
                    <AppSvgIcon icon='link' tabIndex={-1} />
                    <span>{formatMessage(walletPageMessages.previnetLinkLabel)}</span>
                  </a>
                </Tooltip>
              )}
              {showFondiPensione && showSustainableFuture && <div className='previnet-link-spacer' />}
              {showSustainableFuture && (
                <Tooltip title={formatMessage(walletPageMessages.sustainableFutureLinkMouseOverLabel)} arrow>
                  <a onClick={handleSustainableFutureClick} onKeyDown={handleKeyDownSustainableFuture} tabIndex={0} role='button'>
                    <AppSvgIcon icon='link' tabIndex={-1} />
                    <span>{formatMessage(walletPageMessages.sustainableFutureLinkLabel)}</span>
                  </a>
                </Tooltip>
              )}
            </div>
          )}
        </div>
        <div className='macro-categories-accordion'>
          {categories
            .filter((c) => (type === 'FinancialInsurance' ? (c as FinancialInsuranceProductCategory)?.products?.length > 0 : true))
            .map((category, index) => (
              <FinancialPositionsCategoryBox key={index} category={category} macroCategory={macroCategory} />
            ))}
        </div>

        {type === 'Managed' && (
          <div className='real-economy-explain'>
            <div>{formatMessage(walletCategoriesMessages.SgrNote)}</div>
          </div>
        )}
        {type === 'RealEconomy' && (
          <div className='real-economy-explain'>
            <div>{formatMessage(labelForRealEconomyExplain())}</div>
            <div>{formatMessage(walletCategoriesMessages.realeEconomyNote1)}</div>
            <div>{formatMessage(walletCategoriesMessages.realeEconomyNote2)}</div>
          </div>
        )}
      </div>
    </>
  );
};
